import { Grid2, Paper, useMediaQuery, useTheme } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid';
import { Invoice } from '@thro-types/billing';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useGetCustomerWithInvoicesQuery } from 'api/hooks/billing/customer';
import { DataGrid, TypedGridColumns } from 'app/components';
import { Searchfield } from 'app/components/Searchfield';
import { translations } from 'locales/i18n';
import { formatMonetaryValue } from 'utils/formatMonetaryValue';
import usePageHeading from 'utils/hooks/usePageHeading';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { InfoCustomerCard, InformationCustomerPart, InvoiceStateChip } from '../../components';
import { DATEFORMAT } from '../../utils';

export const CustomerDetailPage = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    page,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  } = useTableQuery({
    defaultSort: [{ field: 'createdAt', sort: 'desc' }],
    defaultPageSize: 100
  });

  usePageHeading({
    heading: t(translations.pages.billingCustomer.headingDetailPage()),
    headingProps: { back: location.state?.back ?? `billing/customers` }
  });

  const handleRowClick = (row: GridRowParams<Invoice>) => {
    navigate(`/billing/invoices/${row.id}`, { state: { back: location.pathname } });
  };

  const { data, isLoading, isFetching } = useGetCustomerWithInvoicesQuery(id!, {
    params: {
      skip: page * pageSize,
      take: pageSize,
      sort: sortingModel[0]?.field,
      sortOrder: sortingModel[0]?.sort,
      search: debouncedSearchText
    }
  });

  const theme = useTheme();

  const smallerThanXl = useMediaQuery(theme.breakpoints.down('xl'));

  const columns = useMemo((): TypedGridColumns<Invoice> => {
    return [
      {
        field: 'totalGross',
        flex: 1,
        headerName: t(translations.common.brutto()),
        renderCell: params => formatMonetaryValue(params.value)
      },
      {
        field: 'state',
        flex: 1,
        headerName: t(translations.common.state()),
        renderCell: params => (
          <InvoiceStateChip state={params.value} cancelled={!!params.row.cancelledInvoiceId} />
        ),
        align: 'center'
      },
      {
        field: 'outgoingInvoiceNumber',
        flex: 1,
        headerName: t(translations.pages.billing.invoiceNumber())
      },
      {
        field: 'department',
        flex: 2,
        headerName: t(translations.common.department()),
        renderCell: params => params.row.department?.name || ''
      },
      {
        field: 'createdAt',
        flex: 1,
        headerName: t(translations.common.createdAt()),
        renderCell: params => format(new Date(params.value), DATEFORMAT, { locale: de })
      }
    ];
  }, [t]);

  return (
    <Grid2 container spacing={2}>
      {smallerThanXl && (
        <Grid2 size={{ xs: 12 }}>
          <InformationCustomerPart data={data} renderSkeleton={isLoading} />
        </Grid2>
      )}
      <Grid2 size={{ xs: 12, xl: 9 }}>
        <Grid2 container justifyContent={'space-between'} paddingBottom={1}>
          <Searchfield handleSearch={handleSearchTextChange} search={search} />
        </Grid2>
        <Grid2 component={Paper} height={'75vh'}>
          <DataGrid
            columns={columns}
            rows={data?.invoices.data || []}
            rowCount={data?.invoices?.count || 0}
            loading={isFetching}
            onRowClick={handleRowClick}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={handlePaginationChange}
            sortingMode="server"
            sortModel={sortingModel}
            onSortModelChange={handleSortModelChange}
            disableColumnMenu
            disableColumnFilter
          />
        </Grid2>
      </Grid2>
      {!smallerThanXl && (
        <Grid2 size={{ xs: 3 }} justifyContent={'flex-end'}>
          <InfoCustomerCard data={data} renderSkeleton={isLoading} />
        </Grid2>
      )}
    </Grid2>
  );
};
